import type { ReactElement } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { articlesUrlFor, usesArticlesSpace } from "@/features/articles/hooks";
import { useAuthStore } from "@/features/auth/store";

/**
 * Aiguillage de l'espace Articles (ADR 0062). Module actif : `/catalogue` et
 * `/categories` (favoris) mènent à la section correspondante de `/articles`,
 * et la fiche `/catalogue/:id` à `/articles/:id`. Module éteint : ces pages
 * restent celles d'avant, et `/articles` ramène au catalogue. Tant que
 * l'utilisateur n'est pas chargé, rien n'est aiguillé ; la requête de
 * l'adresse (section, filtres) suit toujours.
 */
export function LegacyCatalogueRoute({
  legacy,
  children,
}: {
  legacy: "catalogue" | "categories";
  children: ReactElement;
}) {
  const me = useAuthStore((s) => s.me);
  const location = useLocation();
  if (!me) return null;
  if (!usesArticlesSpace(me)) return children;
  return <Navigate to={articlesUrlFor(legacy, location.search)} replace />;
}

export function LegacyProductRoute({ children }: { children: ReactElement }) {
  const me = useAuthStore((s) => s.me);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  if (!me) return null;
  if (!usesArticlesSpace(me)) return children;
  return <Navigate to={`/articles/${id ?? ""}${location.search}`} replace />;
}

export function ArticlesOnlyRoute({ children, detail = false }: { children: ReactElement; detail?: boolean }) {
  const me = useAuthStore((s) => s.me);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  if (!me) return null;
  if (usesArticlesSpace(me)) return children;
  return <Navigate to={`${detail && id ? `/catalogue/${id}` : "/catalogue"}${location.search}`} replace />;
}
